/**
 * ChatMessageRenderer.jsx  — v7
 * ==============================
 * v7 adds:
 *  - Mixed-scale auto-detection (14M vs 3K → dual Y-axis ComposedChart)
 *  - Mobile responsive: compact layout, hidden axis labels on narrow screens
 *  - More chart types: Composed (bar+line dual axis), Stacked Bar, Scatter
 *  - KPI summary strip (Total / Max / Min / Avg) above chart
 *  - Expand button (fullscreen overlay)
 *  - Download PNG button (html2canvas)
 *  - Smart number formatting (14,000,000 → 14M, 3,500 → 3.5K)
 *  - Client pinnable charts with star button
 * All existing v6 text rendering is 100% unchanged.
 */

import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell,
  ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from "recharts";

/* ── Design tokens ─────────────────────────────────────────────────────── */
const T = {
  bg:          "#2f2f2f",
  surface:     "#252323",
  surfaceAlt:  "#201f1f",
  surfaceHov:  "#202020",
  border:      "#2a2a2a",
  borderStrong:"#3a3a3a",
  borderGold:  "#bca97a",
  gold:        "#e2bf6d",
  goldSoft:    "rgba(226,191,109,0.08)",
  goldDim:     "#7a5c1a",
  text:        "#ffffff",
  textMuted:   "#a0a0a0",
  textDim:     "#555555",
  green:       "#68d877",
  red:         "#ef4646",
  amber:       "#d29922",
  purple:      "#e48bfa",
  teal:        "#00A896",
  riskExtreme: { text: "#f85149", bg: "#200d0d", border: "#6b1a1a" },
  riskHigh:    { text: "#e3b341", bg: "#261d05", border: "#6b4f10" },
  riskMedium:  { text: "#d29922", bg: "#201908", border: "#6b5010" },
  riskLow:     { text: "#3fb950", bg: "#0d2016", border: "#1a5e3c" },
  rank1: { bg: "#1e1600", border: "#e2bf6d", text: "#ffd700" },
  rank2: { bg: "#1a1a1a", border: "#a0a0a0", text: "#c0c0c0" },
  rank3: { bg: "#180f00", border: "#cd7f32", text: "#cd7f32" },
  fontBody: "'Georgia', 'Times New Roman', serif",
  fontMono: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
  fontUI:   "'Trebuchet MS', 'Gill Sans', system-ui, sans-serif",
};

const CHART_PALETTE = [
  T.gold, "#00A896", "#68d877", "#ef4646",
  "#e48bfa", "#d29922", "#378ADD", "#7F77DD",
  "#F5A623", "#1D9E75", "#E24B4A", "#9B59B6",
];

/* ── Number formatting ──────────────────────────────────────────────────── */
function fmtNum(v) {
  const n = Number(v);
  if (isNaN(n)) return v;
  if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (Math.abs(n) >= 1_000)     return (n / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  if (Math.abs(n) < 10)         return n.toFixed(2);
  return n.toLocaleString();
}

function fmtFull(v) { return Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }); }

/* ── Mixed scale detection ──────────────────────────────────────────────── */
function detectMixedScale(data) {
  if (!data || data.length < 2) return false;
  const vals = data.map(d => Math.abs(Number(d.value) || 0)).filter(v => v > 0);
  if (vals.length < 2) return false;
  const max = Math.max(...vals);
  const min = Math.min(...vals);
  return max / min > 100;
}

/* ── useWindowWidth hook ────────────────────────────────────────────────── */
function useWindowWidth() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 800);
  useEffect(() => {
    const handler = () => setW(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return w;
}

/* ── KPI strip ──────────────────────────────────────────────────────────── */
function KPIStrip({ data }) {
  const vals = data.map(d => Number(d.value) || 0);
  const total = vals.reduce((a, b) => a + b, 0);
  const max   = Math.max(...vals);
  const min   = Math.min(...vals);
  const avg   = total / vals.length;
  const kpis  = [
    { label: "Total", value: fmtNum(total) },
    { label: "Max",   value: fmtNum(max) },
    { label: "Min",   value: fmtNum(min) },
    { label: "Avg",   value: fmtNum(avg) },
  ];
  return (
    <div style={{
      display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap",
    }}>
      {kpis.map(k => (
        <div key={k.label} style={{
          flex: "1 1 60px",
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: 6,
          padding: "5px 8px",
          textAlign: "center",
        }}>
          <div style={{ fontSize: 9, color: T.textMuted, fontFamily: T.fontUI,
                        textTransform: "uppercase", letterSpacing: "0.08em" }}>{k.label}</div>
          <div style={{ fontSize: 13, color: T.gold, fontFamily: T.fontMono,
                        fontWeight: 700, marginTop: 2 }}>{k.value}</div>
        </div>
      ))}
    </div>
  );
}

/* ── Tooltip styles ─────────────────────────────────────────────────────── */
function makeTooltipStyle() {
  return {
    backgroundColor: T.surface,
    border: `1px solid ${T.borderStrong}`,
    borderRadius: 6,
    color: T.text,
    fontSize: 11,
    fontFamily: T.fontUI,
  };
}

/* ── CasinoChart ────────────────────────────────────────────────────────── */
const CHART_TYPES  = ["bar", "line", "area", "pie", "composed", "stacked"];
const CHART_LABELS = { bar: "Bar", line: "Line", area: "Area", pie: "Pie",
                       composed: "Dual", stacked: "Stack" };

function CasinoChart({ spec, onPin, isPinned }) {
  const [activeType, setActiveType]   = useState(null);
  const [expanded,   setExpanded]     = useState(false);
  const chartRef = useRef(null);
  const windowW  = useWindowWidth();

  /* ── hooks must all be before any early return ── */
  const handleDownload = useCallback(() => {}, []);

  if (!spec || !spec.type || !Array.isArray(spec.data) || spec.data.length === 0) return null;

  const isMobile    = windowW < 600;
  const chartHeight = expanded ? 420 : isMobile ? 200 : 260;
  const currentType = activeType || (detectMixedScale(spec.data) ? "composed" : spec.type);

  const chartData = spec.data.map(d => ({
    name:  String(d.label),
    value: Number(d.value) || 0,
  }));

  const axisStyle = {
    fontSize: isMobile ? 9 : 10,
    fill: T.textMuted,
    fontFamily: T.fontUI,
  };
  const tooltipStyle = makeTooltipStyle();
  const margin = isMobile
    ? { top: 4, right: 8, left: 0, bottom: 36 }
    : { top: 8, right: 16, left: 0, bottom: 44 };

  /* ── individual chart renderers ── */
  const renderBar = () => (
    <BarChart data={chartData} margin={margin}>
      <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
      <XAxis dataKey="name" tick={axisStyle} angle={-30} textAnchor="end" interval={0}
             hide={isMobile && chartData.length > 6} />
      <YAxis tick={axisStyle} tickFormatter={fmtNum}
             label={spec.y_label && !isMobile ? { value: spec.y_label, angle: -90,
               position: "insideLeft", fill: T.textMuted, fontSize: 10 } : undefined} />
      <Tooltip contentStyle={tooltipStyle}
               formatter={v => [fmtFull(v), spec.y_label || "Value"]}
               labelStyle={{ color: T.gold }} />
      <Bar dataKey="value" radius={[3, 3, 0, 0]}>
        {chartData.map((_, i) => (
          <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
        ))}
      </Bar>
    </BarChart>
  );

  const renderLine = () => (
    <LineChart data={chartData} margin={margin}>
      <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
      <XAxis dataKey="name" tick={axisStyle} angle={-30} textAnchor="end" interval={0}
             hide={isMobile && chartData.length > 8} />
      <YAxis tick={axisStyle} tickFormatter={fmtNum} />
      <Tooltip contentStyle={tooltipStyle}
               formatter={v => [fmtFull(v), spec.y_label || "Value"]}
               labelStyle={{ color: T.gold }} />
      <Line type="monotone" dataKey="value" stroke={T.gold} strokeWidth={2}
            dot={{ r: isMobile ? 2 : 3, fill: T.gold }} activeDot={{ r: 5 }} />
    </LineChart>
  );

  const renderArea = () => (
    <AreaChart data={chartData} margin={margin}>
      <defs>
        <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%"  stopColor={T.gold} stopOpacity={0.3} />
          <stop offset="95%" stopColor={T.gold} stopOpacity={0.02} />
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
      <XAxis dataKey="name" tick={axisStyle} angle={-30} textAnchor="end" interval={0}
             hide={isMobile && chartData.length > 8} />
      <YAxis tick={axisStyle} tickFormatter={fmtNum} />
      <Tooltip contentStyle={tooltipStyle}
               formatter={v => [fmtFull(v), spec.y_label || "Value"]}
               labelStyle={{ color: T.gold }} />
      <Area type="monotone" dataKey="value" stroke={T.gold}
            fill="url(#areaGrad)" strokeWidth={2} />
    </AreaChart>
  );

  const renderPie = () => (
    <PieChart>
      <Pie data={chartData} dataKey="value" nameKey="name"
           cx="50%" cy="50%"
           outerRadius={isMobile ? 65 : 90}
           innerRadius={isMobile ? 28 : 36}
           paddingAngle={2}
           label={({ name, percent }) =>
             isMobile
               ? `${(percent * 100).toFixed(0)}%`
               : `${name.length > 10 ? name.slice(0, 9) + "…" : name} ${(percent * 100).toFixed(0)}%`
           }
           labelLine={!isMobile}>
        {chartData.map((_, i) => (
          <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
        ))}
      </Pie>
      <Tooltip contentStyle={tooltipStyle} formatter={v => fmtFull(v)}
               labelStyle={{ color: T.gold }} />
      {!isMobile && <Legend iconSize={10} wrapperStyle={{ fontSize: 10, color: T.textMuted }} />}
    </PieChart>
  );

  /* Dual axis composed chart — fixes the mixed-scale problem */
  const renderComposed = () => {
    const vals   = chartData.map(d => d.value);
    const maxVal = Math.max(...vals);
    const minVal = Math.min(...vals.filter(v => v > 0));
    const threshold = maxVal / 20;
    const primary   = chartData.map(d => ({ ...d, primary:   d.value > threshold ? d.value : null }));
    const secondary = chartData.map(d => ({ ...d, secondary: d.value <= threshold ? d.value : null }));
    const merged    = chartData.map((d, i) => ({ ...d, ...primary[i], ...secondary[i] }));

    return (
      <ComposedChart data={merged} margin={margin}>
        <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
        <XAxis dataKey="name" tick={axisStyle} angle={-30} textAnchor="end" interval={0}
               hide={isMobile && chartData.length > 6} />
        <YAxis yAxisId="left"  tick={axisStyle} tickFormatter={fmtNum} />
        <YAxis yAxisId="right" orientation="right" tick={axisStyle} tickFormatter={fmtNum} />
        <Tooltip contentStyle={tooltipStyle}
                 formatter={(v, name) => [fmtFull(v), name === "primary" ? "Primary" : "Secondary"]}
                 labelStyle={{ color: T.gold }} />
        <Bar  yAxisId="left"  dataKey="primary"   fill={T.gold}  radius={[3,3,0,0]} name="primary">
          {merged.map((_, i) => <Cell key={i} fill={CHART_PALETTE[i % 4]} />)}
        </Bar>
        <Line yAxisId="right" dataKey="secondary" stroke={T.teal} strokeWidth={2}
              dot={{ r: 3, fill: T.teal }} name="secondary" connectNulls={false} />
      </ComposedChart>
    );
  };

  /* Stacked bar — groups data into two halves */
  const renderStacked = () => {
    const half = Math.ceil(chartData.length / 2);
    const stackData = chartData.slice(0, half).map((d, i) => ({
      name: d.name,
      a: d.value,
      b: chartData[i + half] ? chartData[i + half].value : 0,
      bLabel: chartData[i + half] ? chartData[i + half].name : "",
    }));
    return (
      <BarChart data={stackData} margin={margin}>
        <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
        <XAxis dataKey="name" tick={axisStyle} angle={-30} textAnchor="end" interval={0}
               hide={isMobile && stackData.length > 6} />
        <YAxis tick={axisStyle} tickFormatter={fmtNum} />
        <Tooltip contentStyle={tooltipStyle}
                 formatter={v => fmtFull(v)} labelStyle={{ color: T.gold }} />
        <Legend iconSize={10} wrapperStyle={{ fontSize: 10, color: T.textMuted }} />
        <Bar dataKey="a" stackId="s" fill={T.gold}  radius={[0,0,0,0]} name="Group A" />
        <Bar dataKey="b" stackId="s" fill={T.teal}  radius={[3,3,0,0]} name="Group B" />
      </BarChart>
    );
  };

  const renderChart = () => {
    switch (currentType) {
      case "bar":      return renderBar();
      case "line":     return renderLine();
      case "area":     return renderArea();
      case "pie":      return renderPie();
      case "composed": return renderComposed();
      case "stacked":  return renderStacked();
      default:         return renderBar();
    }
  };

  /* ── Mixed scale warning ── */
  const isMixed = detectMixedScale(spec.data);

  /* ── Expanded overlay ── */
  const chartContent = (
    <div ref={chartRef} style={{
      background: T.surfaceAlt,
      border: `1px solid ${T.borderStrong}`,
      borderRadius: expanded ? 12 : 8,
      padding: isMobile ? "10px 6px 6px" : "12px 10px 8px",
      width: "100%",
    }}>
      {/* ── KPI strip ── */}
      <KPIStrip data={chartData} />

      {/* ── Title + controls row ── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 6, marginBottom: 8,
      }}>
        {/* Title */}
        <span style={{
          fontSize: isMobile ? 10 : 11, color: T.textMuted,
          fontFamily: T.fontUI, letterSpacing: "0.03em", flex: 1, minWidth: 0,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {spec.title || "Chart"}
          {isMixed && <span style={{ marginLeft: 6, fontSize: 9, color: T.teal,
            border: `1px solid ${T.teal}`, borderRadius: 4, padding: "1px 5px" }}>dual scale</span>}
        </span>

        {/* Right controls */}
        <div style={{ display: "flex", gap: 4, alignItems: "center", flexShrink: 0 }}>
          {/* Type toggles */}
          {CHART_TYPES.map(type => {
            const isActive = currentType === type;
            const isAuto   = type === "composed" && isMixed && !activeType;
            return (
              <button key={type} onClick={() => setActiveType(type)} style={{
                fontSize: isMobile ? 9 : 10,
                padding: isMobile ? "2px 6px" : "3px 10px",
                borderRadius: 12,
                border: `1px solid ${isActive ? T.gold : T.borderStrong}`,
                background: isActive ? T.gold : isAuto ? "rgba(0,168,150,0.15)" : "transparent",
                color: isActive ? "#0D1B2A" : T.textMuted,
                fontFamily: T.fontUI,
                fontWeight: isActive ? 700 : 400,
                cursor: "pointer",
                transition: "all 0.15s",
              }}>
                {CHART_LABELS[type]}
              </button>
            );
          })}

          {/* Pin button */}
          <button onClick={() => onPin && onPin(spec)}
            title={isPinned ? "Pinned to dashboard" : "Pin to dashboard"}
            style={{
              background: isPinned ? "rgba(226,191,109,0.15)" : "transparent",
              border: `1px solid ${isPinned ? T.gold : T.borderStrong}`,
              borderRadius: 6, padding: "3px 6px", cursor: "pointer",
              color: isPinned ? T.gold : T.textMuted, fontSize: 14, lineHeight: 1,
            }}>
            {isPinned ? "★" : "☆"}
          </button>

          {/* Expand button */}
          <button onClick={() => setExpanded(e => !e)}
            title={expanded ? "Collapse" : "Expand"}
            style={{
              background: "transparent",
              border: `1px solid ${T.borderStrong}`,
              borderRadius: 6, padding: "3px 6px", cursor: "pointer",
              color: T.textMuted, fontSize: 12, lineHeight: 1,
            }}>
            {expanded ? "⊡" : "⊞"}
          </button>
        </div>
      </div>

      {/* ── Chart ── */}
      <ResponsiveContainer width="100%" height={chartHeight}>
        {renderChart()}
      </ResponsiveContainer>

      {/* ── Mixed scale note ── */}
      {isMixed && currentType === "composed" && (
        <div style={{ fontSize: 9, color: T.textMuted, fontFamily: T.fontUI,
                      marginTop: 4, paddingLeft: 4, opacity: 0.7 }}>
          Large values (bars, left axis) · Small values (line, right axis)
        </div>
      )}
    </div>
  );

  if (expanded) {
    return (
      <div style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.85)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }} onClick={e => { if (e.target === e.currentTarget) setExpanded(false); }}>
        <div style={{ width: "100%", maxWidth: 900 }}>
          {chartContent}
        </div>
      </div>
    );
  }

  return <div style={{ marginTop: 14 }}>{chartContent}</div>;
}

/* ── PinnedDashboard ────────────────────────────────────────────────────── */
/*
  Floats as a collapsible panel at bottom of chat when user has pinned charts.
  Client can build their own dashboard by pinning multiple charts.
*/
function PinnedDashboard({ charts, onUnpin, onClear }) {
  const [open, setOpen] = useState(true);
  const windowW = useWindowWidth();
  const isMobile = windowW < 600;

  if (!charts || charts.length === 0) return null;

  return (
    <div style={{
      marginTop: 20,
      border: `1px solid ${T.borderGold}`,
      borderRadius: 10,
      background: T.surface,
      overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "8px 12px",
        background: T.surfaceAlt,
        borderBottom: open ? `1px solid ${T.border}` : "none",
        cursor: "pointer",
      }} onClick={() => setOpen(o => !o)}>
        <span style={{ fontSize: 11, fontFamily: T.fontUI, color: T.gold,
                       fontWeight: 700, letterSpacing: "0.06em" }}>
          ★ MY DASHBOARD ({charts.length})
        </span>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button onClick={e => { e.stopPropagation(); onClear && onClear(); }}
            style={{ fontSize: 10, background: "transparent", border: "none",
                     color: T.textMuted, cursor: "pointer", padding: "2px 6px" }}>
            Clear all
          </button>
          <span style={{ color: T.textMuted, fontSize: 12 }}>{open ? "▲" : "▼"}</span>
        </div>
      </div>

      {/* Grid */}
      {open && (
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 12,
          padding: 12,
        }}>
          {charts.map((spec, i) => (
            <div key={i} style={{ position: "relative" }}>
              <CasinoChart spec={spec} onPin={() => onUnpin(i)} isPinned={true} />
              <button onClick={() => onUnpin(i)} style={{
                position: "absolute", top: 8, right: 8,
                background: "rgba(239,70,70,0.15)",
                border: `1px solid ${T.red}`,
                borderRadius: 4, padding: "1px 5px",
                color: T.red, fontSize: 10, cursor: "pointer",
              }}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Helpers (text rendering) ──────────────────────────────────────────── */
const isTableRow      = l => l.includes(" | ") || (l.startsWith("| ") && l.endsWith(" |"));
const isBullet        = l => /^[-•*]\s/.test(l.trim());
const isNumberedItem  = l => /^\d+\.\s/.test(l.trim());
const isSectionHeader = l => {
  const t = l.trim();
  return (t.endsWith(":") && t.length < 80 && !t.includes("|") &&
          !t.match(/€[\d,]+/) && !t.match(/[+-]?\d+%/) && !t.match(/^\d/));
};
const parseStatLine = l => {
  const m = l.match(/^([^:]+):\s*(.+)$/);
  return m ? { label: m[1].trim(), value: m[2].trim() } : null;
};
const parseTable = lines => {
  const rows = lines
    .filter(l => l.trim() && !l.match(/^[-|:]+$/))
    .map(l => l.replace(/^\|/, "").replace(/\|$/, "").split("|").map(c => c.trim()));
  return rows.length < 2 ? null : { headers: rows[0], rows: rows.slice(1) };
};
const getRiskLevel = t =>
  /EXTREME/i.test(t) ? "extreme" : /HIGH/i.test(t) ? "high" :
  /MEDIUM/i.test(t)  ? "medium"  : /LOW/i.test(t)  ? "low"  : null;

function InlineText({ text }) {
  if (!text) return null;
  const tokens = text.split(
    /(-€[\d,]+(?:\.\d+)?(?:k|m)?|\+€[\d,]+(?:\.\d+)?(?:k|m)?|€[\d,]+(?:\.\d+)?(?:k|m)?|[+]\d+(?:\.\d+)?%|[-]\d+(?:\.\d+)?%|\d+(?:\.\d+)?%|\d+(?:\.\d+)?x|\bUP\b|\bDOWN\b|EXTREME RISK|HIGH RISK|MEDIUM RISK|LOW RISK)/gi
  );
  return (
    <>
      {tokens.map((tok, i) => {
        if (/^-€/.test(tok)) return <span key={i} style={{ color: T.red,   fontWeight: 700, fontFamily: T.fontMono, fontSize: "0.9em" }}>{tok}</span>;
        if (/^\+€/.test(tok)) return <span key={i} style={{ color: T.green, fontWeight: 700, fontFamily: T.fontMono, fontSize: "0.9em" }}>{tok}</span>;
        if (/^€/.test(tok))   return <span key={i} style={{ color: T.green, fontWeight: 600, fontFamily: T.fontMono, fontSize: "0.9em" }}>{tok}</span>;
        if (/^\+\d+(\.\d+)?%$/.test(tok)) return <span key={i} style={{ color: T.green, fontWeight: 700, fontFamily: T.fontMono, fontSize: "0.88em" }}>{tok}</span>;
        if (/^-\d+(\.\d+)?%$/.test(tok))  return <span key={i} style={{ color: T.red,   fontWeight: 700, fontFamily: T.fontMono, fontSize: "0.88em" }}>{tok}</span>;
        if (/^\d+(\.\d+)?%$/.test(tok))   return <span key={i} style={{ color: T.text, fontFamily: T.fontMono, fontSize: "0.88em" }}>{tok}</span>;
        if (/^\d+(\.\d+)?x$/i.test(tok))  return <span key={i} style={{ color: T.amber, fontWeight: 700, fontFamily: T.fontMono, fontSize: "0.88em" }}>{tok}</span>;
        if (/^UP$/i.test(tok))   return <span key={i} style={{ color: T.green, fontWeight: 700 }}>▲ UP</span>;
        if (/^DOWN$/i.test(tok)) return <span key={i} style={{ color: T.red,   fontWeight: 700 }}>▼ DOWN</span>;
        const rl = getRiskLevel(tok);
        if (rl) {
          const r = T[`risk${rl.charAt(0).toUpperCase() + rl.slice(1)}`];
          return <span key={i} style={{ background: r.bg, color: r.text, border: `1px solid ${r.border}`,
            borderRadius: 3, padding: "1px 7px", fontSize: "0.75em", fontWeight: 800,
            fontFamily: T.fontUI, letterSpacing: "0.07em", textTransform: "uppercase", verticalAlign: "middle" }}>
            {tok.toUpperCase()}</span>;
        }
        return <span key={i} style={{ color: T.text }}>{tok}</span>;
      })}
    </>
  );
}

function SectionHeader({ text }) {
  return (
    <div style={{ marginTop: 22, marginBottom: 10, display: "flex", alignItems: "center", gap: 9 }}>
      <div style={{ width: 3, height: 14, background: T.gold, borderRadius: 2, flexShrink: 0 }} />
      <span style={{ fontSize: 10, fontFamily: T.fontUI, fontWeight: 700, color: T.gold,
                     letterSpacing: "0.13em", textTransform: "uppercase", whiteSpace: "nowrap" }}>
        {text.replace(/:$/, "")}
      </span>
      <div style={{ flex: 1, height: 1, background: `linear-gradient(to right, ${T.borderGold}55, transparent)` }} />
    </div>
  );
}

function MetricCard({ label, value }) {
  const isNeg = /DOWN|-€/.test(value);
  const isPos = /\bUP\b|\+€/.test(value);
  const accent = isNeg ? T.red : isPos ? T.green : T.gold;
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`,
                  borderTop: `2px solid ${accent}`, borderRadius: 6,
                  padding: "10px 13px", flex: "1 1 120px", minWidth: 100 }}>
      <div style={{ fontSize: 9, fontFamily: T.fontUI, color: T.textMuted,
                    textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 17, fontFamily: T.fontMono, fontWeight: 700, color: T.text }}>
        <InlineText text={value} />
      </div>
    </div>
  );
}

function DataTable({ headers, rows }) {
  return (
    <div style={{ overflowX: "auto", margin: "10px 0 14px", border: `1px solid ${T.border}`, borderRadius: 7 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.fontMono, fontSize: 12, color: T.text }}>
        <thead>
          <tr style={{ background: T.surfaceAlt, borderBottom: `1px solid ${T.borderStrong}` }}>
            {headers.map((h, i) => (
              <th key={i} style={{ padding: "9px 12px", textAlign: i === 0 ? "left" : "right",
                fontSize: 9, fontFamily: T.fontUI, color: T.gold, fontWeight: 700,
                letterSpacing: "0.1em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}
              style={{ background: ri % 2 === 0 ? T.bg : T.surface, borderBottom: `1px solid ${T.border}`, transition: "background 0.12s" }}
              onMouseEnter={e => (e.currentTarget.style.background = T.surfaceHov)}
              onMouseLeave={e => (e.currentTarget.style.background = ri % 2 === 0 ? T.bg : T.surface)}>
              {row.map((cell, ci) => (
                <td key={ci} style={{ padding: "7px 12px", textAlign: ci === 0 ? "left" : "right",
                                      whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                  <InlineText text={cell} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RankingTable({ items }) {
  const medals = ["🥇", "🥈", "🥉"];
  return (
    <div style={{ overflowX: "auto", margin: "8px 0 14px", border: `1px solid ${T.border}`, borderRadius: 7, overflow: "hidden" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.fontMono, fontSize: 12 }}>
        <tbody>
          {items.map((item, ri) => {
            const rs = ri < 3 ? T[`rank${ri + 1}`] : null;
            const rowBg = rs ? rs.bg : ri % 2 === 0 ? T.bg : T.surface;
            const split = item.match(/^(.+?)\s*[-–:]\s+(€.+)$/);
            const name = split ? split[1].trim() : item;
            const val  = split ? split[2].trim() : null;
            return (
              <tr key={ri}
                style={{ background: rowBg, borderLeft: rs ? `3px solid ${rs.border}` : "3px solid transparent",
                         borderBottom: `1px solid ${T.border}`, transition: "background 0.12s" }}
                onMouseEnter={e => (e.currentTarget.style.background = T.surfaceHov)}
                onMouseLeave={e => (e.currentTarget.style.background = rowBg)}>
                <td style={{ padding: "9px 10px", width: 40, textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap" }}>
                  {ri < 3 ? <span style={{ fontSize: 18 }}>{medals[ri]}</span>
                           : <span style={{ fontFamily: T.fontMono, fontSize: 10, color: T.textMuted, fontWeight: 700 }}>#{ri + 1}</span>}
                </td>
                <td style={{ padding: "9px 12px", color: rs ? rs.text : T.text, fontWeight: rs ? 600 : 400, verticalAlign: "middle" }}>
                  <InlineText text={name} />
                </td>
                {val && (
                  <td style={{ padding: "9px 12px", textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", verticalAlign: "middle" }}>
                    <InlineText text={val} />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function BulletItem({ text, ordered, index }) {
  const content = text.replace(/^[-•*]\s*|\d+\.\s*/, "").trim();
  return (
    <div style={{ display: "flex", gap: 9, padding: "3px 0", alignItems: "flex-start",
                  fontSize: 13, lineHeight: 1.65, color: T.text }}>
      {ordered
        ? <span style={{ color: T.gold, fontFamily: T.fontMono, fontSize: 11, fontWeight: 700,
                         marginTop: 2, minWidth: 18, textAlign: "right", flexShrink: 0 }}>{index}.</span>
        : <span style={{ color: T.goldDim, marginTop: 7, flexShrink: 0, fontSize: 6 }}>◆</span>}
      <span><InlineText text={content} /></span>
    </div>
  );
}

function ComparisonRow({ text }) {
  const parts = text.split("|").map(p => p.trim());
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center",
                  padding: "7px 0", borderBottom: `1px solid ${T.border}` }}>
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span style={{ color: T.border, margin: "0 10px", fontSize: 14 }}>│</span>}
          <span style={{ fontSize: 13, color: T.text }}><InlineText text={part} /></span>
        </React.Fragment>
      ))}
    </div>
  );
}

function parseBlocks(text) {
  const lines = text.split("\n").map(l => l.trimEnd());
  const blocks = [];
  let i = 0;
  const isRankingList = items =>
    items.length >= 2 && items.every((item, idx) => {
      const m = item.trim().match(/^(\d+)\.\s/);
      return m && parseInt(m[1]) === idx + 1;
    });
  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line) { i++; continue; }
    if (/^#{1,3}\s/.test(line)) { blocks.push({ type: "section_header", text: line.replace(/^#{1,3}\s/, "") + ":" }); i++; continue; }
    if (isTableRow(line)) {
      const tl = [];
      while (i < lines.length && (isTableRow(lines[i].trim()) || lines[i].trim().match(/^[-|:]+$/))) tl.push(lines[i++]);
      const parsed = parseTable(tl);
      if (parsed) blocks.push({ type: "table", ...parsed });
      continue;
    }
    if (line.includes(" | ") && !line.startsWith("|")) { blocks.push({ type: "comparison", text: line }); i++; continue; }
    if (isSectionHeader(line)) { blocks.push({ type: "section_header", text: line }); i++; continue; }
    if (isNumberedItem(line)) {
      const items = [];
      while (i < lines.length) {
        const l = lines[i].trim();
        if (isNumberedItem(l)) { items.push(l); i++; }
        else if (!l && items.length) { i++; break; }
        else break;
      }
      blocks.push(isRankingList(items)
        ? { type: "ranking", items: items.map(it => it.replace(/^\d+\.\s*/, "")) }
        : { type: "ordered_bullets", items });
      continue;
    }
    if (isBullet(line)) {
      const items = [];
      while (i < lines.length && isBullet(lines[i].trim())) items.push(lines[i++].trim());
      blocks.push({ type: "bullets", items }); continue;
    }
    const stat = parseStatLine(line);
    if (stat && stat.value.match(/^[€\d+\-]/) && i + 1 < lines.length) {
      const nextStat = parseStatLine((lines[i + 1] || "").trim());
      if (nextStat) {
        const metrics = [];
        while (i < lines.length) {
          const ml = lines[i].trim();
          if (!ml) { i++; break; }
          const m = parseStatLine(ml);
          if (!m) break;
          metrics.push(m); i++;
        }
        if (metrics.length >= 2) { blocks.push({ type: "metric_cards", metrics }); continue; }
        blocks.push({ type: "text", text: metrics.map(m => `${m.label}: ${m.value}`).join("\n") }); continue;
      }
    }
    blocks.push({ type: "text", text: line }); i++;
  }
  return blocks;
}

/* ── Main export ────────────────────────────────────────────────────────── */
export default function ChatMessageRenderer({ text, chartSpec }) {
  const [pinnedCharts, setPinnedCharts] = useState([]);
  const blocks = useMemo(() => parseBlocks(text || ""), [text]);

  const handlePin = useCallback((spec) => {
    setPinnedCharts(prev => {
      const already = prev.findIndex(s => s.title === spec.title);
      if (already >= 0) return prev.filter((_, i) => i !== already);
      return [...prev, spec];
    });
  }, []);

  const handleUnpin = useCallback((idx) => {
    setPinnedCharts(prev => prev.filter((_, i) => i !== idx));
  }, []);

  if (!text) return null;

  const isPinned = chartSpec
    ? pinnedCharts.some(s => s.title === chartSpec.title)
    : false;

  return (
    <div style={{ color: T.text, fontFamily: T.fontBody, fontSize: 13, lineHeight: 1.7, maxWidth: "100%" }}>
      {blocks.map((block, bi) => {
        switch (block.type) {
          case "section_header":
            return <SectionHeader key={bi} text={block.text} />;
          case "metric_cards":
            return (
              <div key={bi} style={{ display: "flex", flexWrap: "wrap", gap: 7, margin: "9px 0 13px" }}>
                {block.metrics.map((m, mi) => <MetricCard key={mi} label={m.label} value={m.value} />)}
              </div>
            );
          case "table":   return <DataTable   key={bi} headers={block.headers} rows={block.rows} />;
          case "ranking": return <RankingTable key={bi} items={block.items} />;
          case "comparison": return <ComparisonRow key={bi} text={block.text} />;
          case "bullets":
            return (
              <div key={bi} style={{ margin: "4px 0 10px" }}>
                {block.items.map((item, ii) => <BulletItem key={ii} text={item} />)}
              </div>
            );
          case "ordered_bullets":
            return (
              <div key={bi} style={{ margin: "4px 0 10px" }}>
                {block.items.map((item, ii) => <BulletItem key={ii} text={item} ordered index={ii + 1} />)}
              </div>
            );
          default:
            return (
              <p key={bi} style={{ margin: "3px 0 7px", color: T.text, fontSize: 13 }}>
                <InlineText text={block.text} />
              </p>
            );
        }
      })}

      {/* Chart + pin */}
      {chartSpec && (
        <CasinoChart
          spec={chartSpec}
          onPin={handlePin}
          isPinned={isPinned}
        />
      )}

      {/* Pinned dashboard */}
      <PinnedDashboard
        charts={pinnedCharts}
        onUnpin={handleUnpin}
        onClear={() => setPinnedCharts([])}
      />
    </div>
  );
}